<template>
  <div>
    <div>
      <div class="flex flex-col mb-4">
        <p class="checklist-builder__second-stage-title">
          {{ $t('template-builder.access-report-viewers.title') }}
        </p>
        <p class="text-sm text-70">
          {{ $t('template-builder.access-report-viewers.description') }}
        </p>
      </div>
    </div>

    <div v-if="!isOnFreePlan">
      <BigField
        :class="viewableByAll ? 'checklist-builder__big-field-users--all-selected' : ''"
      >
        <template v-slot:field>
          <checkbox-field
            class="  "
            :value="viewableByAll"
            :label="$t('template-builder.access-report-viewers.all-users')"
            @input="toggleFillableByAll"
          />
        </template>
      </BigField>

      <div v-if="!viewableByAll">
        <choose-user-field-with-select-all
          v-if="!isOnFreePlan"
          :hide-label="true"
          :options="options"
          :disabled="isOnFreePlan"
          :value="checklistRestrictionOptions"
          :errors="[]"
          :identifier="createUserModelIdentifier.checklistBuilderReportViewer"
          :select-all-enabled="true"
          :deselect-all-enabled="true"
          @input="setViewabilityOptions"
        />
      </div>
    </div>

    <div v-else>
      <upgrade-ad :message="$t('upgrade-ad.cb-stage2')"
                  is-vertical
                  with-dropdown
                  :placeholder="$t('All users')" />
    </div>
  </div>
</template>

<script>
  import _ from 'lodash';
  import BigField from '@/_legacy/views/ChecklistBuilder/BigField.vue';
  import SelectControl from '@/_legacy/components/Controls/SelectControl.vue';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { eventBus } from '@/_legacy/eventBus';
  import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
  import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
  import ChooseUserFieldWithSelectAll from '@/ui/components/user/ChooseUserFieldWithSelectAll';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import { CheckboxField } from '@/ui/components/form';
  import { Icon } from '@/ui/components/icon/Icon';
  import { t } from '@/ui/i18n/i18n';
  import { filterListToEditableGroups, filterListToEditableUsers, separateUsersAndGroups } from '@/ui/helpers';

  export default {
    components: {
      CheckboxField,
      UpgradeAd,
      BigField,
      SelectControl,
      ChooseUserFieldWithSelectAll,
      Icon,
    },

    mixins: [
      authMixin,
    ],

    props: {
      reportViewers: {
        type: Object,
        default: null,
      },
      groups: {
        type: Array,
        required: true,
      },
      users: {
        type: Array,
        required: true,
      },
    },

    data() {
      return {
        freePlanModalMessage: {
          text: this.$t('This feature is not included in your free plan.'),
          advice: this.$t('Upgrade now to hide this checklist from specific users, groups or entity items'),
        },
        reportRestrictions: {
          groups: [],
          users: [],
          entity_items: [],
          form_filler: [],
        },
        formFiller: {
          value: 'form_filler',
          label: t('common.form-filler'),
        },
        createUserModelIdentifier: CreateUserModelIdentifier,
        unWatchOptions: null,
        mergedOptions: [],
      };
    },

    computed: {
      viewableByAll() {
        return this.checklist.viewableByAll;
      },

      viewableBySubmitter() {
        return this.checklist.viewableBySubmitter;
      },

      options() {
        const users = this.users.map(user => ({ ...user, value: `user_${user.value}` }));
        const groups = this.groups.map(group => ({ ...group, value: `group_${group.value}` }));

        const finalOptions = [...users, ...groups];

        finalOptions.unshift(this.formFiller);

        this.mergedOptions = finalOptions;

        return finalOptions;
      },

      everybody() {
        return _.isEmpty(this.reportRestrictions.groups)
          && _.isEmpty(this.reportRestrictions.users)
          && !this.viewableBySubmitter;
      },

      checklistRestrictionOptions() {
        const { users, groups, form_filler } = this.reportRestrictions;

        const options = this.getOptions(users, groups);

        if (form_filler?.length || this.viewableBySubmitter) {
          options.unshift(this.formFiller.value);
        }

        return options;
      },

      checklist() {
        return this.$store.getters['checklist_builder/checklist'];
      },
    },

    watch: {
      viewableByAll: {
        deep: false,
        handler(viewableByAll) {
          if (!viewableByAll) {
            trackClick(CLICK_EVENTS.RESTRICT_AVAILABILITY);
          }
        },
      },
      checklistRestrictionOptions: {
        deep: false,
        handler(checklistRestrictionOptions) {
          this.$store.commit(
            'checklist_builder/setViewableBySubmitter',
            Boolean(checklistRestrictionOptions.find(value => value === this.formFiller.value))
          );
        }
      }
    },

    mounted() {
      this.reportRestrictions = this.reportViewers ?? {
        groups: [],
        users: [],
        entity_items: [],
        form_filler: [],
      };

      if (this.viewableBySubmitter) {
        this.reportRestrictions.form_filler = [this.formFiller.value];
      }
    },

    methods: {
      toggleFillableByAll() {
        this.$store.commit('checklist_builder/setViewableByAll', !this.viewableByAll);
      },

      onUserCreated({ response, identifier }) {
        if (CreateUserModelIdentifier.checklistBuilderUserRestriction !== identifier) {
          return;
        }

        this.setAvailabilityOptions(options.value.push(`user_${response.id}`));
      },
      selectAllUsers() {
        const options = this.getOptions(this.users, this.groups).map(option => option.value);
        this.setViewabilityOptions(options);
      },
      deselectAllUsers() {
        this.setViewabilityOptions([]);
      },

      displayFreePlanModal() {
        eventBus.$emit('free-plan-limit', { message: this.freePlanModalMessage });
      },

      setViewabilityOptions(options) {
        this.$store.commit('checklist_builder/everybodyCanViewReport', _.isEmpty(options));

        let { users, groups } = separateUsersAndGroups(options);

        users = filterListToEditableUsers(users).map(id => Number(id));
        groups = filterListToEditableGroups(groups).map(id => Number(id));
        const formFiller = options.find(value => value === 'form_filler');

        this.$set(this.reportRestrictions, 'users', users);
        this.$set(this.reportRestrictions, 'groups', groups);
        this.$set(this.reportRestrictions, 'form_filler', formFiller || []);
        this.$store.commit('checklist_builder/setViewableBySubmitter', !!formFiller);

        !users.length && this.$set(this.reportRestrictions, 'users', []);
        !groups.length && this.$set(this.reportRestrictions, 'groups', []);

        if (!this.everybody) {
          this.$store.commit('checklist_builder/setReportRestrictions', this.reportRestrictions);
        }
      },

      getOptions(users, groups) {
        users = users.map(user => `user_${user}`);
        groups = groups.map(group => `group_${group}`);

        return [...users, ...groups];
      },
    }
  };
</script>

<style scoped>
.availability > div {
  @apply py-2;
}
</style>
